import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import StopIcon from "@mui/icons-material/Stop";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import GroupsIcon from "@mui/icons-material/Groups";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import MicIcon from "@mui/icons-material/Mic";
import VideocamIcon from "@mui/icons-material/Videocam";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import SortIcon from "@mui/icons-material/Sort";
import CachedIcon from "@mui/icons-material/Cached";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import { LiveTrainingTableSwapDialog } from "./LiveTrainingTableSwapDialog";
import { LiveTrainingTableMoveDialog } from "./LiveTrainingTableMoveDialog";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import {
  formatPlayerName,
  formatPlayerStats,
  playerDisplayName,
  resolvePlayerDisplayFields,
} from "../../utils/liveTrainingPlayerContext";
import { computeSubmissionSummary } from "../../utils/kotCoachRound";
import {
  canCoachShowResultButton,
  coachResultButtonLabelKey,
  isRealKotMatchId,
} from "../../utils/kotCoachSubmit";
import { useBrand } from "../../context/BrandContext";
import { LiveTrainingClubLogo } from "./LiveTrainingClubLogo";
import { CoachTablePreviewCard } from "./CoachTablePreviewCard";
import { LiveTrainingTableSetupPingfloVoice } from "./LiveTrainingTableSetupPingfloVoice";
import { LiveTrainingStationTabs } from "./LiveTrainingStationTabs";
import "./CoachTablePreviewCard.css";
import { calculateMHR } from "../../features/trainingSummary/zoneUtils";
import {
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../../utils/coachExerciseNavigation";
import { deriveCoachControlIdleTimers } from "../../coaching/coachExerciseLiveActions";
import { isTeSequences } from "../../utils/teExecutionType";
import { resolveLiveSequence } from "../../utils/liveTrainingSequenceResolve";
import {
  useExerciseRemainingSeconds,
  useKotRoundElapsedSeconds,
  useSequenceRemainingSeconds,
} from "../../hooks/useKotHeroTimers";
import {
  canExchangeSeatedPlayers,
  getUnassignedPresentPlayers,
} from "../../utils/coachSessionTables";
import { identityUnconfirmed } from "../../utils/tablePlayerIdentity";
import "./LiveTrainingTableSetupWizard.css";
import "./LiveTrainingCoachTablesDialog.css";

function formatCoachTablesTimer(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "--:--";
  const s = Math.max(0, Math.floor(Number(seconds)));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/**
 * Live timer for table configuration.
 * compact: icon + mm:ss for the header stats row.
 */
function CoachTablesConfigTimer({
  exercise,
  exerciseStateId,
  isKot = false,
  kotSnapshot = null,
  awaitingManualSequenceStart = false,
  awaitingAutoSequenceStart = false,
  sequenceTransitionPauseRemaining = null,
  currentSequenceIndex = null,
  compact = false,
  t,
}) {
  const isActive =
    exerciseStateId === TE_STATE_STARTED || exerciseStateId === TE_STATE_PAUSED;
  const isPaused = exerciseStateId === TE_STATE_PAUSED;
  const isBetweenSeq =
    (!!awaitingManualSequenceStart || !!awaitingAutoSequenceStart) &&
    isTeSequences(exercise) &&
    !isKot;
  const idleTimers =
    !isActive && exercise ? deriveCoachControlIdleTimers(exercise) : null;
  const hasSequences =
    isTeSequences(exercise) && !isKot && (exercise?.sequences?.length || 0) > 0;
  const kotCoachUi = kotSnapshot?.coach_ui ?? null;
  const kotRoundState = kotSnapshot?.current_round?.round_state ?? null;
  const kotBetweenRounds =
    kotRoundState === "PENDING" || kotCoachUi?.awaiting_coach_start === true;
  const kotRoundClosing =
    kotCoachUi?.round_closing === true ||
    kotCoachUi?.all_tables_submitted === true;
  const kotModeActive =
    isKot && isActive && !isBetweenSeq && !kotBetweenRounds && !kotRoundClosing;

  const roundElapsed = useKotRoundElapsedSeconds(
    isKot && !kotBetweenRounds && !kotRoundClosing ? kotSnapshot : null
  );
  const exerciseRemainingLive = useExerciseRemainingSeconds(
    exercise,
    isActive,
    isPaused
  );
  const sequenceRemainingLive = useSequenceRemainingSeconds(
    exercise,
    isActive,
    isPaused
  );

  const ringShowsSequenceTimer =
    hasSequences && isActive && !isBetweenSeq && !kotModeActive;

  const rawTimer = kotModeActive
    ? roundElapsed
    : isBetweenSeq && awaitingAutoSequenceStart
      ? sequenceTransitionPauseRemaining ?? 0
      : isBetweenSeq
        ? (exerciseRemainingLive ?? exercise?.timer ?? 0)
        : idleTimers
          ? idleTimers.timer
          : ringShowsSequenceTimer
            ? (sequenceRemainingLive ?? exercise?.sequenceTimer ?? 0)
            : (exerciseRemainingLive ?? exercise?.timer ?? 0);

  const timerSec = rawTimer;
  const urgent =
    isActive &&
    !isBetweenSeq &&
    !kotModeActive &&
    timerSec > 0 &&
    timerSec <= 30;

  let label = t("liveTrainingReady") || "Ready";
  if (kotModeActive) {
    const roundNumber = kotSnapshot?.current_round?.round_number;
    label =
      roundNumber != null
        ? t("kotRoundNumberLabel", {
            n: roundNumber,
            defaultValue: `Round #${roundNumber}`,
          })
        : t("kotRoundElapsed") || "Round";
  } else if (isBetweenSeq && awaitingAutoSequenceStart) {
    label = t("sequenceTransitionPause") || "Next sequence in";
  } else if (isBetweenSeq) {
    label = t("exercise") || "Exercise";
  } else if (isPaused) {
    label = t("paused") || "Paused";
  } else if (ringShowsSequenceTimer) {
    const seqNum = resolveLiveSequence(exercise, currentSequenceIndex).number;
    label = `${t("liveTrainingSequence") || "Sequence"} ${seqNum}`;
  } else if (isActive) {
    label = t("live") || "Live";
  }

  if (!exercise) return null;

  if (compact) {
    return (
      <span
        className={`lt-coach-tables-dialog__stat lt-coach-tables-dialog__stat--timer${
          urgent ? " is-urgent" : ""
        }${isPaused ? " is-paused" : ""}`}
        role="timer"
        aria-live="polite"
        aria-label={label}
        title={label}
      >
        <TimerOutlinedIcon fontSize="inherit" />
        {formatCoachTablesTimer(timerSec)}
      </span>
    );
  }

  return (
    <div
      className={`lt-coach-tables-dialog__timer${
        urgent ? " lt-coach-tables-dialog__timer--urgent" : ""
      }${isPaused ? " lt-coach-tables-dialog__timer--paused" : ""}`}
      role="timer"
      aria-live="polite"
      aria-label={label}
    >
      <span className="lt-coach-tables-dialog__timer-label">{label}</span>
      <span className="lt-coach-tables-dialog__timer-value">
        {formatCoachTablesTimer(timerSec)}
      </span>
    </div>
  );
}

const TABLES_PER_PAGE_MOBILE = 5;

/** Mobile: 5×1. Desktop/tablet: more columns while keeping stable page slots. */
function resolveTablesLayout() {
  if (typeof window === "undefined") {
    return { tablesPerPage: TABLES_PER_PAGE_MOBILE, columns: 1 };
  }
  const w = Math.round(
    window.visualViewport?.width || window.innerWidth || 390
  );
  if (w >= 1100) return { tablesPerPage: 9, columns: 3 };
  if (w >= 720) return { tablesPerPage: 6, columns: 2 };
  return { tablesPerPage: TABLES_PER_PAGE_MOBILE, columns: 1 };
}

function chunkTables(list, size) {
  const pages = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages.length ? pages : [[]];
}

/** Pad each page to a fixed slot count so table blocks never reflow when seats change. */
function padPageSlots(pageItems, pageSize) {
  const slots = Array.from({ length: pageSize }, (_, i) => pageItems[i] ?? null);
  return slots;
}

function tableAccentColor(_tableNumber, tsgColor) {
  const custom = String(tsgColor || "").trim();
  if (custom) return custom;
  return "var(--lt-brand, var(--brand-primary, #0877bc))";
}

function sortTablesByNumber(tables) {
  return [...(tables || [])].sort((a, b) => {
    const an = Number(a?.tableNumber ?? a?.table_number ?? 0);
    const bn = Number(b?.tableNumber ?? b?.table_number ?? 0);
    return an - bn;
  });
}

function sortMatchesByTable(matches) {
  return [...(matches || [])].sort((a, b) => {
    const an = Number(a?.table_number ?? 0);
    const bn = Number(b?.table_number ?? 0);
    return an - bn;
  });
}

function inferWinnerSide(match) {
  if (match?.winner_player_id && match?.player_a_id) {
    if (String(match.winner_player_id) === String(match.player_a_id)) return "A";
    if (String(match.winner_player_id) === String(match.player_b_id)) return "B";
  }
  const a = Number(match?.player_a_score);
  const b = Number(match?.player_b_score);
  if (Number.isFinite(a) && Number.isFinite(b)) {
    if (a > b) return "A";
    if (b > a) return "B";
  }
  return null;
}

function isTerminalMatch(match) {
  return match?.match_state === "WON" || match?.match_state === "ABANDONED";
}

function MatchHeadExtra({
  match,
  isWon,
  showResultBtn,
  labelKey,
  isStopping,
  isSubmitting,
  submitErr,
  scoreEntryTap,
  t,
  onEnterResult,
}) {
  if (match?.is_lone_waiting || match?.match_state === "WAITING") {
    return (
      <span className="lt-coach-tables-dialog__status-pill lt-coach-tables-dialog__status-pill--waiting">
        {t("kotLonePlayerWaitingTitle") || "Waiting for opponent"}
      </span>
    );
  }
  if (isSubmitting) {
    return (
      <span className="lt-coach-dash__kot-submitting" role="status">
        {t("submitting") || "Submitting"}
      </span>
    );
  }
  if (submitErr) {
    const errText =
      submitErr.startsWith("kot") ? t(submitErr) || submitErr : submitErr;
    return (
      <button
        type="button"
        className="lt-coach-dash__kot-submit-error"
        onClick={onEnterResult}
        title={errText}
      >
        {t("kotCoachSubmitRetry") || "Failed — tap to retry"}
      </button>
    );
  }
  if (isWon) {
    return (
      <span className="lt-coach-dash__kot-done">
        <CheckCircleIcon fontSize="inherit" />
        {t("kotMatchWon") || "Done"}
      </span>
    );
  }
  if (scoreEntryTap) {
    return (
      <span className="lt-coach-table__tap-hint">
        <EditIcon fontSize="inherit" />
        {t("kotCoachScoreEntryTapTable") || "Enter result"}
      </span>
    );
  }
  if (showResultBtn) {
    return (
      <button
        type="button"
        className="lt-coach-dash__kot-stop"
        disabled={isStopping}
        onClick={onEnterResult}
      >
        <StopIcon fontSize="inherit" />
        {isStopping ? t("loading") || "…" : t(labelKey) || t("kotCoachEnterResult")}
      </button>
    );
  }
  return (
    <span className="lt-coach-tables-dialog__status-pill">
      {t("kotMatchInProgress") || "In progress"}
    </span>
  );
}

/**
 * Coach table configuration & status — pairings, winners, score entry.
 */
export function LiveTrainingCoachTablesDialog({
  open,
  onClose,
  isKot = false,
  showKotMatches = false,
  roundState = null,
  activeTables = [],
  kotMatches = [],
  roster = [],
  usedTableCount = 0,
  roomCapacity = 0,
  assignedCount = 0,
  presentCount = 0,
  sessionPresentCount = null,
  unseatedPlayers = null,
  otherStationPlayers = [],
  stationLabelsById = null,
  stations = null,
  focusedTsgId = null,
  onSelectStation = null,
  realtimeByTeId = {},
  loading = false,
  kotSubmit = null,
  kotRound = null,
  kotSnapshot = null,
  resultActionMatchId = null,
  activeMatchId = null,
  onMatchEnterResult,
  onMatchScoreEntry,
  tableSetup = null,
  seatingActionsEnabled = false,
  exerciseLocked = false,
  onOpenPlayerPresence = null,
  showPlayerPresenceShortcut = false,
  playerPresenceDisabled = false,
  playerPresenceDisabledTitle = null,
  showSessionMediaShortcut = false,
  onOpenSessionMedia = null,
  formatPlayerCell = null,
  voiceHoldEnabled = false,
  onPlayerVoiceHold = null,
  globalRecordingMode = null,
  onRecordingModeChange = null,
  activeRecording = null,
  onStartVoiceRecording = null,
  onStartVideoRecording = null,
  onStopCapture = null,
  tableVideoRecording = null,
  tableVideoError = null,
  onToggleTableVideoRecording = null,
  onStartTableVideoRecording = null,
  onStopTableVideoRecording = null,
  onCalibrateCamera = null,
  tablesWithCamera = null,
  replayEnabled = false,
  armedByTable = null,
  evalRecordingByTable = null,
  pendingByTable = null,
  noticeByTable = null,
  onReplayTable = null,
  evaluationVideosByTable = null,
  onPlayEvaluationVideo = null,
  user = null,
  tsId = null,
  teId = null,
  grId = null,
  pingfloVoiceDisabled = false,
  onPingfloSeatingChanged = null,
  presentPlayers = [],
  onVoiceCommand = null,
  getHrSamples = null,
  getLiveBpm = null,
  exercise = null,
  exerciseStateId = null,
  awaitingManualSequenceStart = false,
  awaitingAutoSequenceStart = false,
  sequenceTransitionPauseRemaining = null,
  currentSequenceIndex = null,
  onEditPersonalizedPlayer = null,
  socket = null,
  /** Staff finalized correction: copy seating from previous exercise. */
  showCopyLastSeating = false,
  copyLastSeatingAvailable = false,
  copyLastSeatingBusy = false,
  onCopyLastSeating = null,
  copyLastSeatingDisabledTitle = null,
  /** Stack above session/exercise results overlays (z-index 9999+). */
  elevated = false,
}) {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandSecondary = brand?.color_secondary || "#46528b";
  const [hrTrendTables, setHrTrendTables] = useState({});
  const [identityByTable, setIdentityByTable] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [tablesPerPage, setTablesPerPage] = useState(TABLES_PER_PAGE_MOBILE);
  const [gridColumns, setGridColumns] = useState(1);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [seatingMsg, setSeatingMsg] = useState(null);
  const [moveSelectedPlayers, setMoveSelectedPlayers] = useState([]);
  const [seatingExpand, setSeatingExpand] = useState(null); // 'sort' | 'rotate' | null
  /** Focused seating toolbar: only seat actions visible (swap / move / rotate / sort). */
  const [seatFeaturesOpen, setSeatFeaturesOpen] = useState(false);
  const [stationSwitching, setStationSwitching] = useState(false);
  /** Destination station for cross-station moves (defaults to focused). */
  const [moveDestTsgId, setMoveDestTsgId] = useState(null);
  const [destStationTables, setDestStationTables] = useState([]);
  const [destStationLoading, setDestStationLoading] = useState(false);
  const pagesRef = useRef(null);
  const stationSwitchTimerRef = useRef(null);
  const prevFocusedTsgIdRef = useRef(focusedTsgId);

  useEffect(() => {
    setIdentityByTable({});
  }, [teId]);

  useEffect(() => {
    if (!socket) return undefined;
    const onStatus = (payload = {}) => {
      if (teId && payload.te_id != null && String(payload.te_id) !== String(teId)) {
        return;
      }
      setIdentityByTable((prev) => {
        const next = { ...prev };
        if (payload.table_id != null) next[String(payload.table_id)] = payload;
        const num = Number(payload.table_number);
        if (Number.isFinite(num) && num > 0) next[`n:${num}`] = payload;
        return next;
      });
    };
    socket.on("playerIdentityStatus", onStatus);
    return () => socket.off("playerIdentityStatus", onStatus);
  }, [socket, teId]);

  const tableIdentityUnconfirmed = (tableNumber, tableId) => {
    const row =
      (tableId != null && identityByTable[String(tableId)]) ||
      identityByTable[`n:${Number(tableNumber)}`] ||
      null;
    return identityUnconfirmed(row);
  };

  useEffect(() => {
    if (!open) {
      setSwapDialogOpen(false);
      setMoveDialogOpen(false);
      setSeatingMsg(null);
      setSeatingExpand(null);
      setSeatFeaturesOpen(false);
      setMoveSelectedPlayers([]);
      setStationSwitching(false);
      setMoveDestTsgId(null);
      setDestStationTables([]);
      setDestStationLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (moveSelectedPlayers.length === 0) {
      setMoveDestTsgId(focusedTsgId != null ? String(focusedTsgId) : null);
      setDestStationTables([]);
    }
  }, [focusedTsgId, moveSelectedPlayers.length]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const sync = () => {
      const { tablesPerPage: nextCount, columns } = resolveTablesLayout();
      setTablesPerPage(nextCount);
      setGridColumns(columns);
    };
    sync();
    window.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("resize", sync);
    };
  }, []);

  useEffect(() => {
    if (!open) setPageIndex(0);
  }, [open]);

  // Smooth station switch: reset pager, flash content, clear seating chrome.
  useEffect(() => {
    if (!open) {
      prevFocusedTsgIdRef.current = focusedTsgId;
      return undefined;
    }
    const prev = prevFocusedTsgIdRef.current;
    const next = focusedTsgId;
    prevFocusedTsgIdRef.current = next;
    if (prev == null || next == null || String(prev) === String(next)) {
      return undefined;
    }
    setPageIndex(0);
    setSeatingExpand(null);
    setSeatFeaturesOpen(false);
    setSeatingMsg(null);
    setSwapDialogOpen(false);
    setMoveDialogOpen(false);
    setMoveSelectedPlayers([]);
    setStationSwitching(true);
    if (stationSwitchTimerRef.current) {
      clearTimeout(stationSwitchTimerRef.current);
    }
    stationSwitchTimerRef.current = setTimeout(() => {
      setStationSwitching(false);
      stationSwitchTimerRef.current = null;
    }, 220);
    return () => {
      if (stationSwitchTimerRef.current) {
        clearTimeout(stationSwitchTimerRef.current);
        stationSwitchTimerRef.current = null;
      }
    };
  }, [focusedTsgId, open]);

  const coachMhr = useMemo(() => {
    let age = 30;
    let gender = "male";
    try {
      const raw =
        localStorage.getItem("profilingData") ||
        localStorage.getItem("playerFormData");
      if (raw) {
        const profiling = JSON.parse(raw) || {};
        const genderRaw = profiling.gender ?? user?.gender ?? user?.sex ?? "male";
        gender = String(genderRaw).toLowerCase() === "female" ? "female" : "male";
        const parsedAge = Number(user?.age ?? profiling.age);
        if (Number.isFinite(parsedAge) && parsedAge > 0) age = parsedAge;
      }
    } catch {
      /* keep defaults */
    }
    return calculateMHR({ age, gender });
  }, [user]);

  const toggleHrTrendForTable = (tableKey) => {
    setHrTrendTables((prev) => ({
      ...prev,
      [tableKey]: !prev[tableKey],
    }));
  };

  const resolvePlayerSamples = (player) => {
    if (!getHrSamples || !player) return [];
    return getHrSamples(player.playerId, player.userId);
  };

  const resolvePlayerLiveBpm = (player) => {
    if (!getLiveBpm || !player) return null;
    return getLiveBpm(player.playerId, player.userId);
  };

  const entryMatches = useMemo(
    () =>
      sortMatchesByTable(
        (kotMatches || []).filter(
          (m) =>
            isRealKotMatchId(m.kot_match_id) ||
            m.is_lone_waiting ||
            (m.player_a_id && m.player_b_id && m.table_number != null)
        )
      ),
    [kotMatches]
  );

  const seatingSourceTables = useMemo(() => {
    if (
      seatingActionsEnabled &&
      Array.isArray(tableSetup?.tables) &&
      tableSetup.tables.length > 0
    ) {
      return tableSetup.tables;
    }
    return activeTables;
  }, [seatingActionsEnabled, tableSetup?.tables, activeTables]);

  const visibleTables = useMemo(
    () => sortTablesByNumber(seatingSourceTables),
    [seatingSourceTables]
  );

  const otherStationList = useMemo(
    () => (Array.isArray(otherStationPlayers) ? otherStationPlayers : []),
    [otherStationPlayers]
  );

  const otherStationIds = useMemo(() => {
    const ids = new Set();
    for (const p of otherStationList) {
      const id = p?.player_id ?? p?.playerId ?? p?.id;
      if (id != null && id !== "") ids.add(String(id));
    }
    return ids;
  }, [otherStationList]);

  const liveUnseatedPlayers = useMemo(() => {
    const raw = Array.isArray(unseatedPlayers)
      ? unseatedPlayers
      : getUnassignedPresentPlayers(
          presentPlayers?.length ? presentPlayers : roster,
          tableSetup?.stationRawTables?.length > 0
            ? tableSetup.stationRawTables
            : tableSetup?.rawTables?.length > 0
              ? tableSetup.rawTables
              : seatingSourceTables
        );
    if (!otherStationIds.size) return raw;
    return raw.filter((p) => {
      const id = p?.player_id ?? p?.playerId ?? p?.id;
      return id == null || !otherStationIds.has(String(id));
    });
  }, [
    unseatedPlayers,
    presentPlayers,
    roster,
    tableSetup?.stationRawTables,
    tableSetup?.rawTables,
    seatingSourceTables,
    otherStationIds,
  ]);

  const stationLabelFor = useCallback(
    (tsgId) => {
      if (tsgId == null || tsgId === "") return null;
      const key = String(tsgId);
      if (stationLabelsById && typeof stationLabelsById === "object") {
        return stationLabelsById[key] || stationLabelsById[tsgId] || key;
      }
      return key;
    },
    [stationLabelsById]
  );

  const handleSelectStation = useCallback(
    (tsgId) => {
      if (typeof onSelectStation !== "function") return;
      if (
        focusedTsgId != null &&
        tsgId != null &&
        String(focusedTsgId) === String(tsgId)
      ) {
        return;
      }
      onSelectStation(tsgId);
    },
    [focusedTsgId, onSelectStation]
  );

  const playerIdOf = useCallback((player) => {
    if (!player) return null;
    const id = player.player_id ?? player.playerId ?? player.id ?? null;
    return id != null && id !== "" ? String(id) : null;
  }, []);

  const unassignedSeatOptions = useMemo(
    () =>
      liveUnseatedPlayers
        .map((p) => {
          const playerId = playerIdOf(p);
          if (!playerId) return null;
          return {
            playerId,
            name:
              playerDisplayName(p) ||
              p?.name ||
              t("player") ||
              "Player",
            stats:
              formatPlayerStats(p?.classement, p?.ranking_points, t) || "",
          };
        })
        .filter(Boolean),
    [liveUnseatedPlayers, playerIdOf, t]
  );

  const toggleMovePlayer = useCallback(
    (player, meta = {}) => {
      if (!seatingActionsEnabled || exerciseLocked) return;
      const id = playerIdOf(player);
      if (!id) return;
      setSeatingExpand(null);
      setSeatingMsg(null);
      setMoveSelectedPlayers((prev) => {
        if (prev.some((p) => p.playerId === id)) {
          return prev.filter((p) => p.playerId !== id);
        }
        return [
          ...prev,
          {
            playerId: id,
            name:
              playerDisplayName(player) ||
              player?.name ||
              t("player") ||
              "Player",
            fromTableNumber: meta.fromTableNumber ?? null,
            fromSlot: meta.fromSlot ?? null,
            fromTableId: meta.fromTableId ?? null,
            fromTeId: meta.fromTeId ?? null,
            fromTsgId: meta.fromTsgId ?? null,
          },
        ];
      });
    },
    [exerciseLocked, playerIdOf, seatingActionsEnabled, t]
  );

  const openMoveWithUnseated = useCallback(
    (player) => {
      toggleMovePlayer(player, {
        fromTableNumber: player?.seated_table_number ?? null,
        fromSlot: player?.seated_slot ?? null,
        fromTableId: player?.seated_table_id ?? null,
        fromTeId: player?.seated_te_id ?? null,
        fromTsgId: player?.seated_tsg_id ?? null,
      });
    },
    [toggleMovePlayer]
  );

  const seatingBusyEarly = Boolean(
    tableSetup?.busy || tableSetup?.rotatePending
  );

  const inlineMoveEnabled =
    seatingActionsEnabled &&
    !exerciseLocked &&
    !seatingBusyEarly &&
    !showKotMatches &&
    !globalRecordingMode;

  const moveSelectedIds = useMemo(
    () => new Set(moveSelectedPlayers.map((p) => p.playerId)),
    [moveSelectedPlayers]
  );

  const pickingInlineMove = moveSelectedPlayers.length > 0;
  const multiStation = Array.isArray(stations) && stations.length > 1;
  const effectiveMoveDestTsgId =
    moveDestTsgId != null
      ? String(moveDestTsgId)
      : focusedTsgId != null
        ? String(focusedTsgId)
        : null;
  const movingToOtherStation =
    multiStation &&
    effectiveMoveDestTsgId != null &&
    focusedTsgId != null &&
    String(effectiveMoveDestTsgId) !== String(focusedTsgId);

  useEffect(() => {
    let cancelled = false;
    if (!pickingInlineMove || !movingToOtherStation || !effectiveMoveDestTsgId) {
      setDestStationTables([]);
      setDestStationLoading(false);
      return undefined;
    }
    setDestStationTables([]);
    setDestStationLoading(true);
    void (async () => {
      const rows =
        (await tableSetup?.loadStationTables?.(effectiveMoveDestTsgId)) || [];
      if (cancelled) return;
      setDestStationTables(rows);
      setDestStationLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    pickingInlineMove,
    movingToOtherStation,
    effectiveMoveDestTsgId,
    tableSetup?.loadStationTables,
  ]);

  const crossStationSwapPair = useMemo(() => {
    if (moveSelectedPlayers.length !== 2) return null;
    const [a, b] = moveSelectedPlayers;
    const aTsg = a.fromTsgId != null ? String(a.fromTsgId) : String(focusedTsgId || "");
    const bTsg = b.fromTsgId != null ? String(b.fromTsgId) : String(focusedTsgId || "");
    if (!aTsg || !bTsg || aTsg === bTsg) return null;
    if (!a.fromTableId || !b.fromTableId || !a.fromTeId || !b.fromTeId) return null;
    if (!a.fromSlot || !b.fromSlot) return null;
    if (String(a.fromSlot).startsWith("extra:") || String(b.fromSlot).startsWith("extra:")) {
      return null;
    }
    return { a, b };
  }, [focusedTsgId, moveSelectedPlayers]);

  const commitInlineMove = useCallback(
    async (table, destTsgIdOverride = null) => {
      if (!inlineMoveEnabled || moveSelectedPlayers.length === 0) return;
      const tn = Number(table?.tableNumber);
      const tableId = table?.tableId ?? table?.raw?.table_id ?? null;
      if ((!Number.isFinite(tn) || tn <= 0) && tableId == null) return;
      const destId =
        destTsgIdOverride != null
          ? String(destTsgIdOverride)
          : effectiveMoveDestTsgId;
      const tableRef = { tableNumber: tn, tableId };

      const movers = moveSelectedPlayers.filter((src) => {
        const sameTable =
          (tableId != null &&
            src.fromTableId != null &&
            String(src.fromTableId) === String(tableId)) ||
          (src.fromTableNumber != null && Number(src.fromTableNumber) === tn);
        if (!sameTable) return true;
        const srcTsg =
          src.fromTsgId != null
            ? String(src.fromTsgId)
            : focusedTsgId != null
              ? String(focusedTsgId)
              : null;
        return destId != null && srcTsg != null && destId !== srcTsg;
      });
      if (movers.length === 0) {
        setMoveSelectedPlayers([]);
        return;
      }

      setSeatingMsg(null);
      const sameStation =
        !destId ||
        !focusedTsgId ||
        String(destId) === String(focusedTsgId);
      const result = sameStation
        ? await (tableSetup?.movePlayersForce ||
            ((ids, ref) =>
              ids.length === 1
                ? tableSetup?.movePlayerForce?.(
                    ids[0],
                    ref?.tableNumber ?? ref,
                    ref?.tableId
                  )
                : Promise.resolve({ ok: false })))(
            movers.map((m) => m.playerId),
            tableRef
          )
        : await tableSetup?.movePlayersToStation?.(
            movers.map((m) => m.playerId),
            destId,
            tableRef
          );

      if (result?.ok) {
        const destLabel =
          (stationLabelsById && destId && stationLabelsById[String(destId)]) ||
          destId ||
          "";
        setSeatingMsg(
          t("liveTrainingTableMoveMultiDone", {
            names: movers.map((m) => m.name).join(", "),
            table: tn,
            defaultValue: destLabel
              ? `${movers.map((m) => m.name).join(", ")} → ${destLabel} · table ${tn}`
              : `${movers.map((m) => m.name).join(", ")} → table ${tn}`,
          })
        );
        setMoveSelectedPlayers([]);
        setMoveDestTsgId(focusedTsgId != null ? String(focusedTsgId) : null);
      } else {
        setSeatingMsg(
          tableSetup?.error ||
            result?.message ||
            t("liveTrainingTableMoveError") ||
            "Could not move player."
        );
      }
    },
    [
      effectiveMoveDestTsgId,
      focusedTsgId,
      inlineMoveEnabled,
      moveSelectedPlayers,
      stationLabelsById,
      t,
      tableSetup,
    ]
  );

  const seatUnassignedOnTable = useCallback(
    async (table, player) => {
      if (!inlineMoveEnabled || !player?.playerId) return;
      const tn = Number(table?.tableNumber);
      const tableId = table?.tableId ?? table?.raw?.table_id ?? null;
      if ((!Number.isFinite(tn) || tn <= 0) && (tableId == null || tableId === "")) {
        return;
      }
      setSeatingMsg(null);
      const result = await tableSetup?.movePlayerForce?.(
        player.playerId,
        tn,
        tableId
      );
      if (result?.ok) {
        setMoveSelectedPlayers((prev) =>
          prev.filter((p) => p.playerId !== String(player.playerId))
        );
        setSeatingMsg(
          t("liveTrainingTableSeatUnassignedDone", {
            name: player.name,
            table: result.tableNumber ?? tn,
            defaultValue: `${player.name} seated on table ${result.tableNumber ?? tn}`,
          })
        );
      } else if (result?.reason !== "busy") {
        setSeatingMsg(
          tableSetup?.error ||
            result?.message ||
            t("liveTrainingTableMoveError") ||
            "Could not move player."
        );
      }
    },
    [inlineMoveEnabled, t, tableSetup]
  );

  const commitCrossStationSwap = useCallback(async () => {
    if (!inlineMoveEnabled || !crossStationSwapPair) return;
    const { a, b } = crossStationSwapPair;
    setSeatingMsg(null);
    const result = await tableSetup?.swapAcrossStations?.(
      {
        playerId: a.playerId,
        tableId: a.fromTableId,
        teId: a.fromTeId,
        slot: a.fromSlot,
      },
      {
        playerId: b.playerId,
        tableId: b.fromTableId,
        teId: b.fromTeId,
        slot: b.fromSlot,
      }
    );
    if (result?.ok) {
      setSeatingMsg(
        t("liveTrainingTableSwapStationsDone", {
          a: a.name,
          b: b.name,
          defaultValue: `Swapped ${a.name} ↔ ${b.name} between stations`,
        })
      );
      setMoveSelectedPlayers([]);
    } else {
      setSeatingMsg(
        tableSetup?.error ||
          result?.message ||
          t("liveTrainingTableMoveError") ||
          "Could not swap players."
      );
    }
  }, [crossStationSwapPair, inlineMoveEnabled, t, tableSetup]);

  const handleDestSeat = useCallback(
    (table, slot, player) => {
      if (!inlineMoveEnabled) return;
      const id = playerIdOf(player);
      const tableId = table?.tableId ?? table?.raw?.table_id ?? null;
      const teIdForTable =
        table?.teId ||
        tableSetup?.resolveStationTe?.(effectiveMoveDestTsgId)?.te_id ||
        null;
      if (id && moveSelectedIds.has(id)) {
        toggleMovePlayer(player, {
          fromTableNumber: table?.tableNumber ?? null,
          fromSlot: slot,
          fromTableId: tableId,
          fromTeId: teIdForTable,
          fromTsgId: effectiveMoveDestTsgId,
        });
        return;
      }
      const src =
        moveSelectedPlayers.length === 1 ? moveSelectedPlayers[0] : null;
      const target = {
        playerId: id,
        tableId,
        teId: teIdForTable,
        slot,
      };
      if (
        src &&
        canExchangeSeatedPlayers(
          {
            playerId: src.playerId,
            tableId: src.fromTableId,
            teId: src.fromTeId,
            slot: src.fromSlot,
          },
          target
        )
      ) {
        setSeatingMsg(null);
        void (async () => {
          const result = await tableSetup?.swapAcrossStations?.(
            {
              playerId: src.playerId,
              tableId: src.fromTableId,
              teId: src.fromTeId,
              slot: src.fromSlot,
            },
            target
          );
          if (result?.ok) {
            setSeatingMsg(
              t("liveTrainingTableSwapStationsDone", {
                a: src.name,
                b:
                  playerDisplayName(player) ||
                  player?.name ||
                  t("player") ||
                  "Player",
                defaultValue: `Swapped ${src.name} ↔ ${
                  playerDisplayName(player) || player?.name || "Player"
                }`,
              })
            );
            setMoveSelectedPlayers([]);
            setMoveDestTsgId(
              focusedTsgId != null ? String(focusedTsgId) : null
            );
          } else {
            setSeatingMsg(
              tableSetup?.error ||
                result?.message ||
                t("liveTrainingTableMoveError") ||
                "Could not swap players."
            );
          }
        })();
        return;
      }
      if (!id) {
        void commitInlineMove(table, effectiveMoveDestTsgId);
        return;
      }
      setSeatingMsg(
        moveSelectedPlayers.length > 1
          ? t("liveTrainingTableMoveTapTable") ||
              "Tap the table to move the selected players."
          : t("liveTrainingTableSwapNeedsSeat") ||
              "Tap the table to move. Swap needs two seated players."
      );
    },
    [
      commitInlineMove,
      effectiveMoveDestTsgId,
      focusedTsgId,
      inlineMoveEnabled,
      moveSelectedIds,
      moveSelectedPlayers,
      playerIdOf,
      t,
      tableSetup,
      toggleMovePlayer,
    ]
  );

  const selectMoveDestStation = useCallback(
    (destId) => {
      if (!inlineMoveEnabled || !destId || moveSelectedPlayers.length === 0) {
        return;
      }
      setMoveDestTsgId(String(destId));
      const destLabel =
        (stationLabelsById && stationLabelsById[String(destId)]) || destId;
      if (focusedTsgId != null && String(destId) === String(focusedTsgId)) {
        setSeatingMsg(
          t("liveTrainingTableMovePickTable") ||
            "Tap a table on this station."
        );
        return;
      }
      setSeatingMsg(
        t("liveTrainingTableMovePickDestTableNamed", {
          station: destLabel,
          defaultValue: `Tap a table on ${destLabel}, or Seat now.`,
        })
      );
    },
    [
      focusedTsgId,
      inlineMoveEnabled,
      moveSelectedPlayers.length,
      stationLabelsById,
      t,
    ]
  );

  const quickSeatToStation = useCallback(
    async (destId) => {
      if (!inlineMoveEnabled || !destId || moveSelectedPlayers.length === 0) {
        return;
      }
      setMoveDestTsgId(String(destId));
      setSeatingMsg(null);
      const result = await tableSetup?.movePlayersToStation?.(
        moveSelectedPlayers.map((m) => m.playerId),
        destId,
        null
      );
      if (result?.ok) {
        const destLabel =
          (stationLabelsById && stationLabelsById[String(destId)]) || destId;
        setSeatingMsg(
          t("liveTrainingTableMoveToStationDone", {
            names: moveSelectedPlayers.map((m) => m.name).join(", "),
            station: destLabel,
            table: result.tableNumber,
            defaultValue: `${moveSelectedPlayers
              .map((m) => m.name)
              .join(", ")} → ${destLabel} · table ${result.tableNumber}`,
          })
        );
        setMoveSelectedPlayers([]);
        setMoveDestTsgId(focusedTsgId != null ? String(focusedTsgId) : null);
      } else if (result?.reason === "table_not_found") {
        setSeatingMsg(
          t("liveTrainingNoStationTables") ||
            "No tables assigned to this station."
        );
      } else {
        setSeatingMsg(
          tableSetup?.error ||
            result?.message ||
            t("liveTrainingTableMoveError") ||
            "Could not move player."
        );
      }
    },
    [
      focusedTsgId,
      inlineMoveEnabled,
      moveSelectedPlayers,
      stationLabelsById,
      t,
      tableSetup,
    ]
  );

  const boardTables = useMemo(() => {
    if (movingToOtherStation) return sortTablesByNumber(destStationTables);
    return visibleTables;
  }, [destStationTables, movingToOtherStation, visibleTables]);

  const tablePages = useMemo(
    () => chunkTables(boardTables, tablesPerPage),
    [boardTables, tablesPerPage]
  );

  useEffect(() => {
    setPageIndex(0);
  }, [movingToOtherStation, effectiveMoveDestTsgId]);

  const matchPages = useMemo(
    () => chunkTables(entryMatches, tablesPerPage),
    [entryMatches, tablesPerPage]
  );

  const pages = showKotMatches ? matchPages : tablePages;
  const pageCount = pages.length;
  const safePageIndex = Math.min(pageIndex, Math.max(0, pageCount - 1));

  useEffect(() => {
    if (pageIndex > pageCount - 1) setPageIndex(Math.max(0, pageCount - 1));
  }, [pageIndex, pageCount]);

  const goToPage = useCallback(
    (next) => {
      const clamped = Math.max(0, Math.min(pageCount - 1, next));
      setPageIndex(clamped);
      const scroller = pagesRef.current;
      if (!scroller) return;
      const pageWidth = scroller.clientWidth || 1;
      scroller.scrollTo({ left: clamped * pageWidth, behavior: "smooth" });
    },
    [pageCount]
  );

  useEffect(() => {
    const scroller = pagesRef.current;
    if (!scroller || !open) return undefined;
    const onScroll = () => {
      const pageWidth = scroller.clientWidth || 1;
      const next = Math.round(scroller.scrollLeft / pageWidth);
      setPageIndex((prev) => (prev === next ? prev : next));
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [open, pageCount]);

  useEffect(() => {
    const scroller = pagesRef.current;
    if (!scroller || !open) return;
    const pageWidth = scroller.clientWidth || 1;
    scroller.scrollTo({ left: safePageIndex * pageWidth, behavior: "auto" });
  }, [open, tablesPerPage, safePageIndex]);

  const summary = useMemo(
    () => (showKotMatches ? computeSubmissionSummary(entryMatches) : null),
    [showKotMatches, entryMatches]
  );

  const tableAccentByNumber = useMemo(() => {
    const map = new Map();
    for (const table of visibleTables) {
      const num = Number(table?.tableNumber);
      if (!Number.isFinite(num)) continue;
      map.set(
        num,
        tableAccentColor(num, table?.tsgColor || table?.raw?.tsg_color)
      );
    }
    return map;
  }, [visibleTables]);

  const labelKey = coachResultButtonLabelKey({
    config: kotSnapshot?.config,
    roundState,
  });

  const resolvePlayer =
    formatPlayerCell ||
    ((player, playerId) => {
      const fields = resolvePlayerDisplayFields(player, roster, playerId);
      return {
        name: formatPlayerName(player, roster, playerId),
        playerId: fields.playerId,
        userId: fields.userId,
        classement: fields.classement,
        rankingPoints: fields.rankingPoints,
      };
    });

  const resolveAccent = (tableNumber, explicitColor = null) => {
    const n = Number(tableNumber);
    if (explicitColor) return tableAccentColor(n, explicitColor);
    if (Number.isFinite(n) && tableAccentByNumber.has(n)) {
      return tableAccentByNumber.get(n);
    }
    return tableAccentColor(n, null);
  };

  const resolveSubgroupLabel = (tableNumber, explicitName = null) => {
    if (explicitName) return explicitName;
    const row = visibleTables.find(
      (t) => Number(t?.tableNumber) === Number(tableNumber)
    );
    return row?.tsgName || row?.raw?.tsg_name || null;
  };

  const portalRoot = getLiveTrainingPortalRoot();
  if (!open || !portalRoot) return null;

  const tableCapacity = roomCapacity || activeTables.length || 0;

  const showRecordingTools = voiceHoldEnabled && onRecordingModeChange;
  const showPlayerPresenceTool =
    showPlayerPresenceShortcut && typeof onOpenPlayerPresence === "function";
  const showSessionMediaTool =
    showSessionMediaShortcut && typeof onOpenSessionMedia === "function";
  const tableVideoSelectMode = globalRecordingMode === "video";
  const tableVideoLive = Boolean(tableVideoRecording?.recording);
  const cameraTableSet =
    tablesWithCamera instanceof Set
      ? tablesWithCamera
      : new Set(
          (tablesWithCamera || [])
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n) && n > 0)
        );
  const selectableTableNumbers = (() => {
    const nums = new Set();
    const rows = showKotMatches
      ? (entryMatches || []).filter(Boolean)
      : visibleTables || [];
    for (const row of rows) {
      const n = Number(row?.tableNumber ?? row?.table_number);
      if (Number.isFinite(n) && n > 0) nums.add(n);
    }
    return [...nums].sort((a, b) => a - b);
  })();
  const isTableVideoSelected = (tn) =>
    Boolean(tableVideoRecording?.isTableSelected?.(tn));
  const isTableVideoRecording = (tn) =>
    Boolean(tableVideoRecording?.isTableRecording?.(tn));
  const isTableVideoStarting = (tn) =>
    Boolean(tableVideoRecording?.isTableStarting?.(tn));
  const isTableCameraOnline = (tn) => {
    const n = Number(tn);
    if (!Number.isFinite(n) || n <= 0) return false;
    return (
      isTableVideoRecording(n) ||
      isTableVideoStarting(n) ||
      cameraTableSet.has(n) ||
      Boolean(armedByTable?.[String(n)]) ||
      Boolean(evalRecordingByTable?.[String(n)])
    );
  };
  const tableHasSeatedPlayers = (tn) => {
    const n = Number(tn);
    if (!Number.isFinite(n) || n <= 0) return false;
    if (showKotMatches) {
      return (entryMatches || []).some(
        (m) =>
          Number(m?.table_number) === n &&
          Boolean(m?.player_a_id || m?.player_b_id)
      );
    }
    const row = (visibleTables || []).find(
      (tRow) => Number(tRow?.tableNumber ?? tRow?.table_number) === n
    );
    if (!row) return false;
    const hasP = (p) => Boolean(p?.playerId || p?.player_id || p?.name);
    return (
      hasP(row.player1) ||
      hasP(row.player2) ||
      hasP(row.player3) ||
      (Array.isArray(row.extras) && row.extras.some(hasP))
    );
  };
  const isTableCameraConnected = (tn) => {
    const n = Number(tn);
    if (!Number.isFinite(n) || n <= 0) return false;
    if (isTableCameraOnline(n)) return true;
    if (tableHasSeatedPlayers(n)) return true;
    return selectableTableNumbers.includes(n);
  };
  const recordableTableNumbers = selectableTableNumbers.filter((n) =>
    isTableCameraConnected(n)
  );

  const recordingHint = !showRecordingTools
    ? null
    : tableVideoSelectMode || tableVideoLive
      ? {
          live: tableVideoLive,
          text:
            tableVideoError ||
            (tableVideoLive
              ? t("liveTrainingTableRecordingLive", {
                  count: tableVideoRecording?.recordingCount || 0,
                  defaultValue: `Recording ${
                    tableVideoRecording?.recordingCount || 0
                  } table(s). Tap Stop on a table to save the clip.`,
                })
              : t(
                  "liveTrainingCoachRecordingPickTables",
                  "Tap Record on a table with a camera phone."
                )),
        }
      : globalRecordingMode === "voice" && !activeRecording
        ? {
            live: false,
            text:
              t("liveTrainingCoachRecordingPickVoice") ||
              "Tap a player to start voice recording.",
          }
        : null;

  const seatingBusy = Boolean(tableSetup?.busy || tableSetup?.rotatePending);
  const seatingDisabled = !seatingActionsEnabled || exerciseLocked || seatingBusy;
  const seatFeaturesActive =
    seatingActionsEnabled &&
    (seatFeaturesOpen ||
      pickingInlineMove ||
      seatingExpand != null ||
      swapDialogOpen ||
      moveDialogOpen);
  const exitSeatFeatures = () => {
    setSeatFeaturesOpen(false);
    setSeatingExpand(null);
    setMoveSelectedPlayers([]);
    setSeatingMsg(null);
    setSwapDialogOpen(false);
    setMoveDialogOpen(false);
  };
  const seatingTables = seatingSourceTables;
  const showStationTabs =
    Array.isArray(stations) &&
    stations.length > 1 &&
    typeof onSelectStation === "function";
  const unseatedCount = liveUnseatedPlayers.length;
  const otherStationCount = otherStationList.length;
  const liveAssignedCount =
    typeof assignedCount === "number"
      ? assignedCount
      : Math.max(0, (presentPlayers?.length || 0) - unseatedCount);
  const livePresentCount = Math.max(
    typeof presentCount === "number"
      ? presentCount
      : presentPlayers?.length || 0,
    liveAssignedCount
  );
  const liveSessionPresentCount =
    typeof sessionPresentCount === "number"
      ? sessionPresentCount
      : livePresentCount;

  const runRotate = async (direction) => {
    if (seatingDisabled || !tableSetup?.rotate) return;
    setSeatingMsg(null);
    const ok = await tableSetup.rotate(direction);
    setSeatingMsg(
      ok
        ? t("liveTrainingTableWizardRotateDone", { direction }) ||
            `Rotated ${direction}.`
        : tableSetup.error
    );
  };

  const runSort = async (ascending) => {
    if (seatingDisabled || !tableSetup?.assignByRanking) return;
    setSeatingMsg(null);
    const ok = await tableSetup.assignByRanking(Boolean(ascending));
    setSeatingMsg(
      ok
        ? t("liveTrainingTableWizardAssignDone") ||
            "Players assigned by ranking."
        : tableSetup.error
    );
  };

  const toolBtn = (opts) => (
    <button
      type="button"
      className={[
        "lt-coach-tables-dialog__tool-btn",
        opts.active ? "lt-coach-tables-dialog__tool-btn--active" : "",
        opts.recording ? "lt-coach-tables-dialog__tool-btn--recording" : "",
        opts.emphasis ? "lt-coach-tables-dialog__tool-btn--emphasis" : "",
        opts.pill ? "lt-coach-tables-dialog__tool-btn--pill" : "",
        opts.className || "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={opts.onClick}
      disabled={opts.disabled}
      title={opts.title}
      aria-label={opts.label}
      aria-pressed={opts.active || opts.recording}
    >
      {opts.icon}
      {opts.text ? (
        <span className="lt-coach-tables-dialog__tool-btn-text">
          {opts.text}
        </span>
      ) : null}
      {opts.badge != null ? (
        <span className="lt-coach-tables-dialog__tool-btn-badge">
          {opts.badge}
        </span>
      ) : null}
    </button>
  );

  return createPortal(
    <div
      className={`lt-table-wizard__backdrop lt-coach-tables-dialog__backdrop${
        elevated ? " lt-coach-tables-dialog__backdrop--elevated" : ""
      }`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={`lt-table-wizard lt-coach-tables-dialog${
          gridColumns > 1 ? " lt-coach-tables-dialog--multi-col" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-coach-tables-dialog-title"
        style={{
          "--lt-brand": brandPrimary,
          "--lt-brand-secondary": brandSecondary,
          "--lt-tables-per-page": String(tablesPerPage),
          "--lt-tables-cols": String(gridColumns),
          "--lt-tables-rows": String(Math.ceil(tablesPerPage / gridColumns)),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lt-table-wizard__header lt-coach-tables-dialog__header">
          <div className="lt-coach-tables-dialog__header-main">
            <div className="lt-coach-tables-dialog__title-row">
              <LiveTrainingClubLogo
                user={user}
                size="sm"
                className="lt-coach-tables-dialog__club-logo"
              />
              <h2 id="lt-coach-tables-dialog-title">
                {t("liveTrainingCoachTablesTitle") || "Table configuration"}
              </h2>
              <button
                type="button"
                className="lt-table-wizard__close lt-coach-tables-dialog__close"
                onClick={onClose}
                aria-label={t("close")}
              >
                <CloseIcon />
              </button>
            </div>
            <div
              className="lt-coach-tables-dialog__stats lt-coach-tables-dialog__stats--below"
              aria-label={t("liveTrainingCoachTablesTitle") || "Table configuration"}
            >
              <span
                className="lt-coach-tables-dialog__stat"
                title={t("tables") || "Tables"}
              >
                <TableRestaurantIcon fontSize="inherit" />
                {usedTableCount}/{tableCapacity}
              </span>
              <span
                className="lt-coach-tables-dialog__stat"
                title={
                  t("liveTrainingStationPlayersStat", {
                    assigned: liveAssignedCount,
                    station: livePresentCount,
                    session: liveSessionPresentCount,
                    defaultValue: `${liveAssignedCount} seated on this station · ${livePresentCount} available here · ${liveSessionPresentCount} present in session`,
                  })
                }
              >
                <GroupsIcon fontSize="inherit" />
                {liveAssignedCount}/{livePresentCount}
                {liveSessionPresentCount > livePresentCount ? (
                  <span className="lt-coach-tables-dialog__stat-extra">
                    · {liveSessionPresentCount}
                  </span>
                ) : null}
              </span>
              {unseatedCount > 0 ? (
                <span
                  className="lt-coach-tables-dialog__stat lt-coach-tables-dialog__stat--warn"
                  title={
                    t("liveTrainingTableWizardUnassigned") ||
                    "Unassigned players"
                  }
                >
                  <PersonAddIcon fontSize="inherit" />
                  {unseatedCount}
                </span>
              ) : null}
              <CoachTablesConfigTimer
                compact
                exercise={exercise}
                exerciseStateId={exerciseStateId}
                isKot={isKot}
                kotSnapshot={kotSnapshot}
                awaitingManualSequenceStart={awaitingManualSequenceStart}
                awaitingAutoSequenceStart={awaitingAutoSequenceStart}
                sequenceTransitionPauseRemaining={
                  sequenceTransitionPauseRemaining
                }
                currentSequenceIndex={currentSequenceIndex}
                t={t}
              />
            </div>
            {showStationTabs ? (
              <div className="lt-coach-tables-dialog__stations">
                <LiveTrainingStationTabs
                  stations={stations}
                  focusedTsgId={focusedTsgId}
                  onSelectStation={handleSelectStation}
                  realtimeByTeId={realtimeByTeId}
                />
              </div>
            ) : null}
          </div>
        </header>

        <div className="lt-coach-tables-dialog__chrome">
          <div
            className={`lt-coach-tables-dialog__header-actions${
              seatFeaturesActive
                ? " lt-coach-tables-dialog__header-actions--seat-features"
                : ""
            }`}
            role="toolbar"
            aria-label={
              t("liveTrainingCoachTablesTitle") || "Table configuration"
            }
          >
            {seatingActionsEnabled ? (
              <div
                className={`lt-coach-tables-dialog__tool-group lt-coach-tables-dialog__tool-group--seating${
                  seatFeaturesActive
                    ? " lt-coach-tables-dialog__tool-group--seating-open"
                    : ""
                }`}
                role="group"
                aria-label={
                  t("liveTrainingTableSeatingActions") || "Table seating actions"
                }
              >
                <span className="lt-coach-tables-dialog__tool-group-label">
                  {t("liveTrainingTableSeatingActionsShort", "Seat")}
                </span>
                <div className="lt-coach-tables-dialog__tool-group-row">
                {showCopyLastSeating
                  ? toolBtn({
                      icon: <ContentCopyIcon fontSize="small" />,
                      onClick: () => {
                        if (
                          seatingDisabled ||
                          !copyLastSeatingAvailable ||
                          copyLastSeatingBusy ||
                          typeof onCopyLastSeating !== "function"
                        ) {
                          return;
                        }
                        void onCopyLastSeating();
                      },
                      disabled:
                        seatingDisabled ||
                        !copyLastSeatingAvailable ||
                        copyLastSeatingBusy,
                      label:
                        t("copyLastExerciseSeating") ||
                        "Copy last exercise seating",
                      title: copyLastSeatingAvailable
                        ? t("copyLastExerciseSeatingHint") ||
                          "Copy table seating from the previous exercise"
                        : copyLastSeatingDisabledTitle ||
                          t("copyLastExerciseSeatingDisabled") ||
                          "Previous exercise has no table seating",
                      text: t("copyLastExerciseSeatingShort") || "Copy last",
                      pill: true,
                    })
                  : null}
                {seatFeaturesActive ? (
                  <>
                    {toolBtn({
                      icon: <ArrowBackIcon fontSize="small" />,
                      onClick: exitSeatFeatures,
                      label:
                        t("liveTrainingTableSeatFeaturesDone") ||
                        "Back to tools",
                      title:
                        t("liveTrainingTableSeatFeaturesDone") ||
                        "Back to tools",
                    })}
                    {toolBtn({
                      icon: <SwapHorizIcon fontSize="small" />,
                      onClick: () => {
                        setSeatingExpand(null);
                        setSwapDialogOpen(true);
                      },
                      disabled: seatingDisabled,
                      label:
                        t("liveTrainingTableWizardStepSwap") ||
                        "Switch players",
                      title:
                        t("liveTrainingTableWizardStepSwap") ||
                        "Switch players",
                    })}
                    {toolBtn({
                      icon: <OpenWithIcon fontSize="small" />,
                      active: pickingInlineMove,
                      onClick: () => {
                        setSeatingExpand(null);
                        setMoveSelectedPlayers([]);
                        setMoveDialogOpen(true);
                      },
                      disabled: seatingDisabled,
                      label: t("liveTrainingTableMoveTitle") || "Move player",
                      title:
                        t("liveTrainingTableMoveHint") ||
                        "Move player to a table",
                    })}
                    <div className="lt-coach-tables-dialog__tool-expand">
                      {toolBtn({
                        icon: <CachedIcon fontSize="small" />,
                        active: seatingExpand === "rotate",
                        onClick: () =>
                          setSeatingExpand((prev) =>
                            prev === "rotate" ? null : "rotate"
                          ),
                        disabled: seatingDisabled,
                        label: t("rotate") || "Rotate",
                        title:
                          t("rotate_players") ||
                          t("rotate") ||
                          "Rotate players",
                      })}
                      {seatingExpand === "rotate" ? (
                        <div
                          className="lt-coach-tables-dialog__tool-overlay"
                          role="menu"
                        >
                          <button
                            type="button"
                            className="lt-coach-tables-dialog__tool-overlay-item"
                            role="menuitem"
                            disabled={seatingDisabled}
                            onClick={() => {
                              setSeatingExpand(null);
                              void runRotate("left");
                            }}
                          >
                            <RotateLeftIcon fontSize="small" />
                            <span>{t("rotate_left") || "Left"}</span>
                          </button>
                          <button
                            type="button"
                            className="lt-coach-tables-dialog__tool-overlay-item"
                            role="menuitem"
                            disabled={seatingDisabled}
                            onClick={() => {
                              setSeatingExpand(null);
                              void runRotate("right");
                            }}
                          >
                            <RotateRightIcon fontSize="small" />
                            <span>{t("rotate_right") || "Right"}</span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="lt-coach-tables-dialog__tool-expand">
                      {toolBtn({
                        icon: <SortIcon fontSize="small" />,
                        active: seatingExpand === "sort",
                        onClick: () =>
                          setSeatingExpand((prev) =>
                            prev === "sort" ? null : "sort"
                          ),
                        disabled: seatingDisabled,
                        label: t("sort") || "Sort",
                        title:
                          t("liveTrainingTableWizardApplySort") ||
                          "Sort by ranking",
                      })}
                      {seatingExpand === "sort" ? (
                        <div
                          className="lt-coach-tables-dialog__tool-overlay"
                          role="menu"
                        >
                          <button
                            type="button"
                            className="lt-coach-tables-dialog__tool-overlay-item"
                            role="menuitem"
                            disabled={seatingDisabled}
                            onClick={() => {
                              setSeatingExpand(null);
                              void runSort(false);
                            }}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                            <span>
                              {t("liveTrainingTableWizardSortHigh") ||
                                "High → low"}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="lt-coach-tables-dialog__tool-overlay-item"
                            role="menuitem"
                            disabled={seatingDisabled}
                            onClick={() => {
                              setSeatingExpand(null);
                              void runSort(true);
                            }}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                            <span>
                              {t("liveTrainingTableWizardSortLow") ||
                                "Low → high"}
                            </span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : (
                  toolBtn({
                    icon: <EventSeatIcon fontSize="small" />,
                    onClick: () => {
                      setSeatFeaturesOpen(true);
                      setSeatingExpand(null);
                    },
                    disabled: seatingDisabled,
                    label:
                      t("liveTrainingTableSeatFeatures") || "Seat features",
                    title:
                      t("liveTrainingTableSeatFeaturesHint") ||
                      "Swap, move, rotate, and sort players",
                  })
                )}
                </div>
              </div>
            ) : null}

            {!seatFeaturesActive ? (
              <>
                <div
                  className="lt-coach-tables-dialog__tool-group lt-coach-tables-dialog__tool-group--roster"
                  role="group"
                  aria-label={
                    t("managePlayerPresence") || "Players & voice setup"
                  }
                >
                  <span className="lt-coach-tables-dialog__tool-group-label">
                    {t("liveTrainingTableRosterShort", "Players")}
                  </span>
                  <div className="lt-coach-tables-dialog__tool-group-row">
                    {showPlayerPresenceTool
                      ? toolBtn({
                          icon: <PersonAddIcon fontSize="small" />,
                          onClick: onOpenPlayerPresence,
                          disabled: playerPresenceDisabled,
                          badge: unseatedCount > 0 ? unseatedCount : null,
                          label:
                            t("managePlayerPresence") ||
                            "Manage player presence",
                          title: playerPresenceDisabled
                            ? playerPresenceDisabledTitle ||
                              t("managePlayerPresence") ||
                              "Manage player presence"
                            : t("managePlayerPresence") ||
                              "Add / manage players",
                        })
                      : null}
                    {showSessionMediaTool
                      ? toolBtn({
                          icon: <VideoFileOutlinedIcon fontSize="small" />,
                          onClick: onOpenSessionMedia,
                          label: t("sessionMediaFiles", "Session media"),
                          title:
                            t(
                              "liveTrainingAssociateVideosToTables",
                              "Associate videos to tables"
                            ) || t("sessionMediaFiles", "Session media"),
                        })
                      : null}
                    <LiveTrainingTableSetupPingfloVoice
                      variant="micro"
                      disabled={pingfloVoiceDisabled || loading}
                      tsId={tsId}
                      teId={teId}
                      grId={grId}
                      primaryColor={brandPrimary}
                      onSeatingChanged={onPingfloSeatingChanged}
                      presentPlayers={presentPlayers}
                      groupRoster={roster}
                      coachTables={activeTables}
                      onVoiceCommand={onVoiceCommand}
                      selectedPlayers={moveSelectedPlayers}
                      onContextMessage={setSeatingMsg}
                    />
                  </div>
                </div>

                {showRecordingTools ? (
                  <div
                    className={`lt-coach-tables-dialog__tool-group lt-coach-tables-dialog__tool-group--capture${
                      tableVideoLive || activeRecording?.type === "voice"
                        ? " is-live"
                        : tableVideoSelectMode ||
                            globalRecordingMode === "voice"
                          ? " is-armed"
                          : ""
                    }`}
                    role="group"
                    aria-label={
                      t("liveTrainingVideoRecordingMode") ||
                      "Live capture tools"
                    }
                  >
                    <span className="lt-coach-tables-dialog__tool-group-label">
                      {t("liveTrainingTableCaptureShort", "Film")}
                    </span>
                    <div className="lt-coach-tables-dialog__tool-group-row">
                      {toolBtn({
                        icon: <VideocamIcon fontSize="small" />,
                        active: tableVideoSelectMode && !tableVideoLive,
                        recording: tableVideoLive,
                        pill: true,
                        text: tableVideoLive
                          ? `${t("recording", "Rec")} (${
                              tableVideoRecording?.recordingCount || 1
                            })`
                          : t("liveTrainingVideoModeShort", "Video"),
                        disabled: Boolean(activeRecording),
                        onClick: () => {
                          setSeatingExpand(null);
                          setMoveSelectedPlayers([]);
                          if (tableVideoLive && !tableVideoSelectMode) {
                            onRecordingModeChange("video");
                            return;
                          }
                          onRecordingModeChange((prev) =>
                            prev === "video" ? null : "video"
                          );
                          tableVideoRecording?.resetError?.();
                        },
                        label:
                          t("liveTrainingVideoRecordingMode") ||
                          "Video recording mode",
                        title: t(
                          "liveTrainingVideoRecordTablesHint",
                          "Turn on Video, then tap Record on a table"
                        ),
                      })}
                      {(tableVideoSelectMode || tableVideoLive) &&
                      recordableTableNumbers.length > 1 ? (
                        tableVideoLive ? (
                          <button
                            type="button"
                            className="lt-coach-tables-dialog__capture-batch-btn lt-coach-tables-dialog__capture-batch-btn--stop"
                            disabled={
                              tableVideoRecording?.status === "stopping"
                            }
                            onClick={() => {
                              if (
                                typeof onStopTableVideoRecording === "function"
                              ) {
                                onStopTableVideoRecording(null);
                              } else {
                                tableVideoRecording?.stop?.();
                              }
                            }}
                            title={
                              t("liveTrainingStopAllTableRecording") ||
                              "Stop all recordings"
                            }
                          >
                            <StopIcon fontSize="inherit" />
                            <span>
                              {t("liveTrainingStopAllShort", "Stop all")}
                            </span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="lt-coach-tables-dialog__capture-batch-btn"
                            disabled={
                              tableVideoRecording?.status === "starting"
                            }
                            onClick={() => {
                              const toStart = recordableTableNumbers.filter(
                                (n) => !isTableVideoRecording(n)
                              );
                              if (!toStart.length) return;
                              if (
                                typeof onStartTableVideoRecording === "function"
                              ) {
                                onStartTableVideoRecording(toStart);
                              } else {
                                tableVideoRecording?.start?.(toStart);
                              }
                            }}
                            title={
                              t("liveTrainingStartAllTableRecording") ||
                              "Record all table cameras"
                            }
                          >
                            <FiberManualRecordIcon fontSize="inherit" />
                            <span>
                              {t("liveTrainingRecordAllShort", "Record all")}
                            </span>
                          </button>
                        )
                      ) : null}
                      {toolBtn({
                        icon: <MicIcon fontSize="small" />,
                        active:
                          globalRecordingMode === "voice" &&
                          activeRecording?.type !== "voice",
                        recording: activeRecording?.type === "voice",
                        disabled: Boolean(activeRecording) || tableVideoLive,
                        onClick: () => {
                          setSeatingExpand(null);
                          setMoveSelectedPlayers([]);
                          onRecordingModeChange((prev) =>
                            prev === "voice" ? null : "voice"
                          );
                        },
                        label:
                          t("liveTrainingFeedbackVoiceHoldHint") ||
                          "Voice recording mode",
                        title:
                          t("liveTrainingFeedbackVoiceHoldHint") ||
                          "Voice recording mode",
                      })}
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>

        {showKotMatches && summary ? (
          <div className="lt-coach-tables-dialog__progress" role="status">
            <span className="lt-coach-tables-dialog__progress-label">
              {t("kotCoachScoreEntryProgress", {
                submitted: summary.submitted,
                total: summary.total,
                defaultValue: `${summary.submitted}/${summary.total} tables submitted`,
              })}
            </span>
            {summary.allSubmitted ? (
              <span className="lt-coach-tables-dialog__progress-done">
                <CheckCircleIcon fontSize="inherit" />
                {t("kotAllTablesSubmitted") || "All tables submitted"}
              </span>
            ) : null}
          </div>
        ) : null}

        {!showKotMatches && pickingInlineMove && multiStation ? (
          <section
            className="lt-coach-tables-dialog__station-move"
            aria-label={
              t("liveTrainingTableMoveToStation") || "Move to station"
            }
          >
            <div className="lt-coach-tables-dialog__station-move-head">
              <h3>
                {t("liveTrainingTableMoveToStation") || "Send to station"}
              </h3>
              {crossStationSwapPair ? (
                <button
                  type="button"
                  className="lt-coach-tables-dialog__station-swap-btn"
                  disabled={seatingBusyEarly}
                  onClick={() => void commitCrossStationSwap()}
                >
                  <SwapHorizIcon fontSize="small" />
                  {t("liveTrainingTableSwapStations") || "Swap stations"}
                </button>
              ) : null}
            </div>
            <div className="lt-coach-tables-dialog__station-move-list" role="group">
              {(stations || []).map((station) => {
                const id = String(station.tsg_id);
                const selected = effectiveMoveDestTsgId === id;
                const label =
                  (stationLabelsById && stationLabelsById[id]) ||
                  station.name ||
                  id;
                return (
                  <button
                    key={id}
                    type="button"
                    className={`lt-coach-tables-dialog__station-move-btn${
                      selected
                        ? " lt-coach-tables-dialog__station-move-btn--selected"
                        : ""
                    }`}
                    disabled={seatingBusyEarly}
                    onClick={() => selectMoveDestStation(id)}
                    aria-pressed={selected}
                    title={
                      t("liveTrainingTableMoveToStationHint", {
                        station: label,
                        defaultValue: `Choose ${label} as destination`,
                      })
                    }
                  >
                    {label}
                  </button>
                );
              })}
              {movingToOtherStation && effectiveMoveDestTsgId ? (
                <button
                  type="button"
                  className="lt-coach-tables-dialog__station-move-btn lt-coach-tables-dialog__station-move-btn--quick"
                  disabled={seatingBusyEarly || destStationLoading}
                  onClick={() => void quickSeatToStation(effectiveMoveDestTsgId)}
                >
                  {t("liveTrainingTableSeatNow") || "Seat now"}
                </button>
              ) : null}
            </div>
            <p className="lt-coach-tables-dialog__dest-tables-hint">
              {movingToOtherStation
                ? t("liveTrainingTableMoveOrSwapDest", {
                    station:
                      (stationLabelsById &&
                        effectiveMoveDestTsgId &&
                        stationLabelsById[String(effectiveMoveDestTsgId)]) ||
                      effectiveMoveDestTsgId,
                    defaultValue:
                      "Tables below are that station. Tap a table to move, or tap a player to swap.",
                  })
                : t("liveTrainingTableMovePickStation") ||
                  "Choose a station. Its tables become the board: tap a table to move, or a player to swap."}
            </p>
          </section>
        ) : null}

        {!showKotMatches && otherStationCount > 0 ? (
          <section
            className="lt-coach-tables-dialog__unseated lt-coach-tables-dialog__unseated--other-station"
            aria-label={
              t("liveTrainingTableMoveOtherStation") ||
              "Players on other stations"
            }
          >
            <div className="lt-coach-tables-dialog__unseated-head">
              <h3>
                {t("liveTrainingTableMoveOtherStation") ||
                  "Players on other stations"}
              </h3>
              <span className="lt-coach-tables-dialog__unseated-count">
                {otherStationCount}
              </span>
            </div>
            <ul className="lt-coach-tables-dialog__unseated-list">
              {otherStationList.map((p) => {
                const pid = playerIdOf(p);
                const fromLabel = stationLabelFor(p.seated_tsg_id);
                const canSeat = inlineMoveEnabled;
                const selected = pid && moveSelectedIds.has(pid);
                return (
                  <li key={`other-${pid ?? playerDisplayName(p)}`}>
                    {canSeat ? (
                      <button
                        type="button"
                        className={`lt-coach-tables-dialog__unseated-btn${
                          selected
                            ? " lt-coach-tables-dialog__unseated-btn--selected"
                            : ""
                        }`}
                        onClick={() => openMoveWithUnseated(p)}
                        aria-pressed={Boolean(selected)}
                        title={
                          t("liveTrainingTableMoveOtherStationHint") ||
                          "Tap to select, then send to a station or swap"
                        }
                      >
                        <span>
                          {playerDisplayName(p)}
                          {fromLabel ? ` · ${fromLabel}` : ""}
                        </span>
                      </button>
                    ) : (
                      <div className="lt-coach-tables-dialog__unseated-row">
                        <span>
                          {playerDisplayName(p)}
                          {fromLabel ? ` · ${fromLabel}` : ""}
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {!showKotMatches && unseatedCount > 0 ? (
          <section
            className="lt-coach-tables-dialog__unseated"
            aria-label={
              t("liveTrainingTableWizardUnassigned") || "Unassigned players"
            }
          >
            <div className="lt-coach-tables-dialog__unseated-head">
              <h3>
                {t("liveTrainingTableWizardUnassigned") ||
                  "Unassigned players"}
              </h3>
              <span className="lt-coach-tables-dialog__unseated-count">
                {unseatedCount}
              </span>
            </div>
            <ul className="lt-coach-tables-dialog__unseated-list">
              {liveUnseatedPlayers.map((p) => {
                const pid = playerIdOf(p);
                const stats =
                  formatPlayerStats(p?.classement, p?.ranking_points, t) ||
                  p?.ranking_points ||
                  "";
                const canSeat = inlineMoveEnabled;
                const selected = pid && moveSelectedIds.has(pid);
                return (
                  <li key={pid ?? playerDisplayName(p)}>
                    {canSeat ? (
                      <button
                        type="button"
                        className={`lt-coach-tables-dialog__unseated-btn${
                          selected
                            ? " lt-coach-tables-dialog__unseated-btn--selected"
                            : ""
                        }`}
                        onClick={() => openMoveWithUnseated(p)}
                        aria-pressed={Boolean(selected)}
                        title={
                          t("liveTrainingTableMoveInlineHint") ||
                          "Tap a player, then tap a table to move them."
                        }
                      >
                        <span>{playerDisplayName(p)}</span>
                        {stats ? <span>{stats}</span> : null}
                      </button>
                    ) : (
                      <div className="lt-coach-tables-dialog__unseated-row">
                        <span>{playerDisplayName(p)}</span>
                        {stats ? <span>{stats}</span> : null}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        <div
          className={`lt-table-wizard__body lt-coach-tables-dialog__body${
            stationSwitching ? " lt-coach-tables-dialog__body--switching" : ""
          }`}
          key={
            focusedTsgId != null ? `station-${focusedTsgId}` : "station-default"
          }
        >
          <div className="lt-coach-tables-dialog__body-main">
          {movingToOtherStation ? (
            <p className="lt-coach-tables-dialog__dest-banner" role="status">
              {(stationLabelsById &&
                effectiveMoveDestTsgId &&
                stationLabelsById[String(effectiveMoveDestTsgId)]) ||
                effectiveMoveDestTsgId}
              {" · "}
              {t("liveTrainingTableMoveOrSwapDestShort") ||
                "Tap a table to move. Tap a player to swap."}
            </p>
          ) : null}
          {loading || (movingToOtherStation && destStationLoading) ? (
            <p className="lt-table-wizard__loading">{t("loading") || "Loading…"}</p>
          ) : (showKotMatches
              ? entryMatches.length === 0
              : boardTables.length === 0) ? (
            <p className="lt-table-wizard__empty-msg">
              {showKotMatches
                ? t("kotNoMatchesYet") || "Waiting for round tables…"
                : isKot
                  ? t("liveTrainingKotNoTablesYet") ||
                    "No table pairings yet. Use setup to assign players."
                  : t("liveTrainingNoStationTables") ||
                    "No tables assigned to this station. Assign tables in planning, or switch station."}
            </p>
          ) : (
            <div className="lt-coach-tables-dialog__pager">
              {pageCount > 1 ? (
                <button
                  type="button"
                  className="lt-coach-tables-dialog__edge lt-coach-tables-dialog__edge--prev"
                  onClick={() => goToPage(safePageIndex - 1)}
                  disabled={safePageIndex <= 0}
                  aria-label={t("previous", "Previous")}
                >
                  <ChevronLeftIcon />
                </button>
              ) : null}
              <div
                className="lt-coach-tables-dialog__pages"
                ref={pagesRef}
                role="region"
                aria-label={t("tables") || "Tables"}
              >
                {pages.map((pageItems, pageIdx) => {
                  const slots = padPageSlots(pageItems, tablesPerPage);
                  return (
                    <div
                      key={`page-${pageIdx}`}
                      className="lt-coach-tables-dialog__page"
                    >
                      <div
                        className="lt-coach-tables-dialog__grid"
                        style={{
                          gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                        }}
                      >
                        {showKotMatches
                          ? slots.map((m, slotIdx) => {
                              if (!m) {
                                return (
                                  <div
                                    key={`empty-${pageIdx}-${slotIdx}`}
                                    className="lt-coach-table lt-coach-table--slot-empty"
                                    aria-hidden
                                  />
                                );
                              }
                              const isWon = isTerminalMatch(m);
                              const winnerSide = inferWinnerSide(m);
                              const showResultBtn = canCoachShowResultButton({
                                match: m,
                                roundState,
                              });
                              const isSubmitting =
                                kotSubmit?.isMatchSubmitting?.(m.kot_match_id);
                              const submitErr =
                                kotSubmit?.getMatchSubmitError?.(
                                  m.kot_match_id
                                );
                              const isStopping =
                                resultActionMatchId === m.kot_match_id &&
                                kotRound?.stoppingRound;
                              const isBusy = isSubmitting || isStopping;
                              const scoreEntryMode =
                                roundState === "STOP_REQUESTED";
                              const selected =
                                activeMatchId != null &&
                                String(activeMatchId) ===
                                  String(m.kot_match_id);
                              const tableKey = String(m.kot_match_id);
                              const p1 = resolvePlayer(
                                m.player_a,
                                m.player_a_id
                              );
                              const p2 = resolvePlayer(
                                m.player_b,
                                m.player_b_id
                              );
                              const accent = resolveAccent(m.table_number);
                              const subgroup = resolveSubgroupLabel(
                                m.table_number
                              );

                              return (
                                <CoachTablePreviewCard
                                  key={m.kot_match_id}
                                  tableNumber={m.table_number}
                                  identityUnconfirmed={tableIdentityUnconfirmed(
                                    m.table_number
                                  )}
                                  player1={p1}
                                  player2={p2}
                                  t={t}
                                  className="lt-table-wizard__card"
                                  accentColor={accent}
                                  subgroupLabel={subgroup}
                                  done={isWon}
                                  disabled={isBusy}
                                  selected={selected}
                                  scoreA={isWon ? m.player_a_score : null}
                                  scoreB={isWon ? m.player_b_score : null}
                                  winnerSide={winnerSide}
                                  voiceHoldEnabled={voiceHoldEnabled}
                                  onPlayerVoiceHold={onPlayerVoiceHold}
                                  globalRecordingMode={globalRecordingMode}
                                  activeRecording={activeRecording}
                                  onStartVoiceRecording={onStartVoiceRecording}
                                  onStartVideoRecording={onStartVideoRecording}
                                  onStopCapture={onStopCapture}
                                  tableVideoSelected={isTableVideoSelected(
                                    m.table_number
                                  )}
                                  tableVideoRecording={isTableVideoRecording(
                                    m.table_number
                                  )}
                                  tableVideoStarting={isTableVideoStarting(
                                    m.table_number
                                  )}
                                  tableCameraConnected={isTableCameraConnected(
                                    m.table_number
                                  )}
                                  tableCameraOnline={isTableCameraOnline(
                                    m.table_number
                                  )}
                                  onToggleTableVideo={
                                    onToggleTableVideoRecording
                                      ? ({ tableNumber: tn }) =>
                                          onToggleTableVideoRecording(
                                            tn ?? m.table_number
                                          )
                                      : null
                                  }
                                  onStartTableVideo={
                                    onStartTableVideoRecording
                                      ? ({ tableNumber: tn }) =>
                                          onStartTableVideoRecording(
                                            tn ?? m.table_number
                                          )
                                      : null
                                  }
                                  onStopTableVideo={
                                    onStopTableVideoRecording
                                      ? ({ tableNumber: tn }) =>
                                          onStopTableVideoRecording(
                                            tn ?? m.table_number
                                          )
                                      : null
                                  }
                                  onCalibrateCamera={onCalibrateCamera}
                                  replayEnabled={replayEnabled}
                                  replayArmed={Boolean(
                                    armedByTable?.[String(m.table_number)]
                                  )}
                                  evalRecording={Boolean(
                                    evalRecordingByTable?.[String(m.table_number)]
                                  )}
                                  replayStatus={
                                    pendingByTable?.[String(m.table_number)] ||
                                    null
                                  }
                                  replayNotice={
                                    noticeByTable?.[String(m.table_number)] ||
                                    null
                                  }
                                  onReplayTable={(payload) =>
                                    onReplayTable?.({
                                      tableNumber:
                                        payload?.tableNumber ?? m.table_number,
                                    })
                                  }
                                  evaluationVideoCount={
                                    evaluationVideosByTable?.[
                                      String(m.table_number)
                                    ]?.length || 0
                                  }
                                  onPlayEvaluationVideo={onPlayEvaluationVideo}
                                  hrTrendMode={Boolean(hrTrendTables[tableKey])}
                                  onToggleHrTrend={() =>
                                    toggleHrTrendForTable(tableKey)
                                  }
                                  player1Samples={resolvePlayerSamples(p1)}
                                  player2Samples={resolvePlayerSamples(p2)}
                                  player1LiveBpm={resolvePlayerLiveBpm(p1)}
                                  player2LiveBpm={resolvePlayerLiveBpm(p2)}
                                  mhr={coachMhr}
                                  onTableClick={
                                    isWon || isBusy
                                      ? undefined
                                      : () => {
                                          if (scoreEntryMode) {
                                            onMatchScoreEntry?.(m);
                                          } else if (showResultBtn) {
                                            onMatchEnterResult?.(m);
                                          }
                                        }
                                  }
                                  headExtra={
                                    <MatchHeadExtra
                                      match={m}
                                      isWon={isWon}
                                      showResultBtn={
                                        showResultBtn && !scoreEntryMode
                                      }
                                      labelKey={labelKey}
                                      isStopping={isStopping}
                                      isSubmitting={isSubmitting}
                                      submitErr={submitErr}
                                      scoreEntryTap={scoreEntryMode && !isWon}
                                      t={t}
                                      onEnterResult={() =>
                                        scoreEntryMode
                                          ? onMatchScoreEntry?.(m)
                                          : onMatchEnterResult?.(m)
                                      }
                                    />
                                  }
                                />
                              );
                            })
                          : slots.map((table, slotIdx) => {
                              if (!table) {
                                return (
                                  <div
                                    key={`empty-${pageIdx}-${slotIdx}`}
                                    className="lt-coach-table lt-coach-table--slot-empty"
                                    aria-hidden
                                  />
                                );
                              }
                              const tableKey = String(
                                table.tableId || table.tableNumber
                              );
                              const accent = resolveAccent(
                                table.tableNumber,
                                table.tsgColor || table.raw?.tsg_color
                              );
                              const subgroup =
                                table.tsgName || table.raw?.tsg_name || null;
                              const p1Id = playerIdOf(table.player1);
                              const p2Id = playerIdOf(table.player2);
                              const p3Id = playerIdOf(table.player3);
                              const p1Selected = p1Id && moveSelectedIds.has(p1Id);
                              const p2Selected = p2Id && moveSelectedIds.has(p2Id);
                              const p3Selected = p3Id && moveSelectedIds.has(p3Id);
                              const extraSelected = (table.extras || []).some((p) =>
                                moveSelectedIds.has(playerIdOf(p))
                              );
                              const isSourceTable =
                                p1Selected ||
                                p2Selected ||
                                p3Selected ||
                                extraSelected;
                              const selectedSlots = [
                                p1Selected ? "player1" : null,
                                p2Selected ? "player2" : null,
                                p3Selected ? "player3" : null,
                                ...(table.extras || []).map((p, i) =>
                                  moveSelectedIds.has(playerIdOf(p))
                                    ? `extra:${i}`
                                    : null
                                ),
                              ].filter(Boolean);
                              const showingDest = movingToOtherStation;
                              const isTargetTable =
                                !showingDest &&
                                inlineMoveEnabled &&
                                pickingInlineMove &&
                                !isSourceTable;
                              const moveSelectMode =
                                inlineMoveEnabled &&
                                (showingDest || !isTargetTable);
                              const destTeId =
                                table.teId ||
                                tableSetup?.resolveStationTe?.(
                                  effectiveMoveDestTsgId
                                )?.te_id ||
                                null;
                              const swapSource =
                                showingDest && moveSelectedPlayers.length === 1
                                  ? moveSelectedPlayers[0]
                                  : null;
                              const seatIsSwapTarget = (slot, player) =>
                                Boolean(
                                  swapSource &&
                                    canExchangeSeatedPlayers(
                                      {
                                        playerId: swapSource.playerId,
                                        tableId: swapSource.fromTableId,
                                        teId: swapSource.fromTeId,
                                        slot: swapSource.fromSlot,
                                      },
                                      {
                                        playerId: playerIdOf(player),
                                        tableId:
                                          table.tableId ||
                                          table.raw?.table_id ||
                                          null,
                                        teId: destTeId,
                                        slot,
                                      }
                                    )
                                );
                              const destSwapSlots = showingDest
                                ? [
                                    seatIsSwapTarget("player1", table.player1)
                                      ? "player1"
                                      : null,
                                    seatIsSwapTarget("player2", table.player2)
                                      ? "player2"
                                      : null,
                                    seatIsSwapTarget("player3", table.player3)
                                      ? "player3"
                                      : null,
                                  ].filter(Boolean)
                                : [];
                              return (
                                <CoachTablePreviewCard
                                  key={table.tableId || table.tableNumber}
                                  tableNumber={table.tableNumber}
                                  identityUnconfirmed={tableIdentityUnconfirmed(
                                    table.tableNumber,
                                    table.tableId || table.raw?.table_id
                                  )}
                                  player1={table.player1}
                                  player2={table.player2}
                                  player3={table.player3}
                                  extras={table.extras}
                                  unassignedPlayers={
                                    movingToOtherStation
                                      ? null
                                      : unassignedSeatOptions
                                  }
                                  onAssignUnassigned={
                                    inlineMoveEnabled && !movingToOtherStation
                                      ? (player) => {
                                          void seatUnassignedOnTable(
                                            table,
                                            player
                                          );
                                        }
                                      : null
                                  }
                                  t={t}
                                  className={[
                                    "lt-table-wizard__card",
                                    isSourceTable
                                      ? "lt-table-swap-dialog__card--source-table"
                                      : isTargetTable || showingDest
                                        ? "lt-table-swap-dialog__card--target-table"
                                        : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  accentColor={accent}
                                  subgroupLabel={subgroup}
                                  swapMode={moveSelectMode}
                                  hideSwapBadges
                                  selected={isSourceTable}
                                  selectedSlot={
                                    selectedSlots.length > 0
                                      ? selectedSlots
                                      : null
                                  }
                                  targetSlots={
                                    destSwapSlots.length ? destSwapSlots : null
                                  }
                                  disabled={
                                    exerciseLocked || seatingBusyEarly
                                  }
                                  onSelectPlayer1={
                                    showingDest
                                      ? () =>
                                          handleDestSeat(
                                            table,
                                            "player1",
                                            table.player1
                                          )
                                      : moveSelectMode
                                        ? () =>
                                            toggleMovePlayer(table.player1, {
                                              fromTableNumber: table.tableNumber,
                                              fromSlot: "player1",
                                              fromTableId:
                                                table.tableId ||
                                                table.raw?.table_id ||
                                                null,
                                              fromTeId: teId,
                                              fromTsgId:
                                                focusedTsgId ||
                                                table.tsgId ||
                                                table.raw?.tsg_id ||
                                                null,
                                            })
                                        : undefined
                                  }
                                  onSelectPlayer2={
                                    showingDest
                                      ? () =>
                                          handleDestSeat(
                                            table,
                                            "player2",
                                            table.player2
                                          )
                                      : moveSelectMode
                                        ? () =>
                                            toggleMovePlayer(table.player2, {
                                              fromTableNumber: table.tableNumber,
                                              fromSlot: "player2",
                                              fromTableId:
                                                table.tableId ||
                                                table.raw?.table_id ||
                                                null,
                                              fromTeId: teId,
                                              fromTsgId:
                                                focusedTsgId ||
                                                table.tsgId ||
                                                table.raw?.tsg_id ||
                                                null,
                                            })
                                        : undefined
                                  }
                                  onSelectPlayer3={
                                    showingDest
                                      ? () =>
                                          handleDestSeat(
                                            table,
                                            "player3",
                                            table.player3
                                          )
                                      : moveSelectMode
                                        ? () =>
                                            toggleMovePlayer(table.player3, {
                                              fromTableNumber: table.tableNumber,
                                              fromSlot: "player3",
                                              fromTableId:
                                                table.tableId ||
                                                table.raw?.table_id ||
                                                null,
                                              fromTeId: teId,
                                              fromTsgId:
                                                focusedTsgId ||
                                                table.tsgId ||
                                                table.raw?.tsg_id ||
                                                null,
                                            })
                                        : undefined
                                  }
                                  onSelectExtra={
                                    showingDest
                                      ? (player, index) =>
                                          handleDestSeat(
                                            table,
                                            `extra:${index}`,
                                            player
                                          )
                                      : moveSelectMode
                                        ? (player, index) =>
                                            toggleMovePlayer(player, {
                                              fromTableNumber: table.tableNumber,
                                              fromSlot: `extra:${index}`,
                                              fromTableId:
                                                table.tableId ||
                                                table.raw?.table_id ||
                                                null,
                                              fromTeId: teId,
                                              fromTsgId:
                                                focusedTsgId ||
                                                table.tsgId ||
                                                table.raw?.tsg_id ||
                                                null,
                                            })
                                        : undefined
                                  }
                                  onTableClick={
                                    showingDest
                                      ? () =>
                                          commitInlineMove(
                                            table,
                                            effectiveMoveDestTsgId
                                          )
                                      : isTargetTable
                                        ? () => commitInlineMove(table)
                                        : undefined
                                  }
                                  voiceHoldEnabled={
                                    voiceHoldEnabled && !pickingInlineMove
                                  }
                                  onPlayerVoiceHold={onPlayerVoiceHold}
                                  globalRecordingMode={
                                    pickingInlineMove
                                      ? null
                                      : globalRecordingMode
                                  }
                                  activeRecording={activeRecording}
                                  onStartVoiceRecording={onStartVoiceRecording}
                                  onStartVideoRecording={onStartVideoRecording}
                                  onStopCapture={onStopCapture}
                                  tableVideoSelected={
                                    !pickingInlineMove &&
                                    isTableVideoSelected(table.tableNumber)
                                  }
                                  tableVideoRecording={
                                    !pickingInlineMove &&
                                    isTableVideoRecording(table.tableNumber)
                                  }
                                  tableVideoStarting={
                                    !pickingInlineMove &&
                                    isTableVideoStarting(table.tableNumber)
                                  }
                                  tableCameraConnected={
                                    !pickingInlineMove &&
                                    isTableCameraConnected(table.tableNumber)
                                  }
                                  tableCameraOnline={
                                    !pickingInlineMove &&
                                    isTableCameraOnline(table.tableNumber)
                                  }
                                  onToggleTableVideo={
                                    !pickingInlineMove &&
                                    onToggleTableVideoRecording
                                      ? ({ tableNumber: tn }) =>
                                          onToggleTableVideoRecording(
                                            tn ?? table.tableNumber
                                          )
                                      : null
                                  }
                                  onStartTableVideo={
                                    !pickingInlineMove &&
                                    onStartTableVideoRecording
                                      ? ({ tableNumber: tn }) =>
                                          onStartTableVideoRecording(
                                            tn ?? table.tableNumber
                                          )
                                      : null
                                  }
                                  onStopTableVideo={
                                    !pickingInlineMove &&
                                    onStopTableVideoRecording
                                      ? ({ tableNumber: tn }) =>
                                          onStopTableVideoRecording(
                                            tn ?? table.tableNumber
                                          )
                                      : null
                                  }
                                  onCalibrateCamera={
                                    pickingInlineMove ? null : onCalibrateCamera
                                  }
                                  replayEnabled={
                                    replayEnabled && !pickingInlineMove
                                  }
                                  replayArmed={Boolean(
                                    armedByTable?.[String(table.tableNumber)]
                                  )}
                                  evalRecording={Boolean(
                                    evalRecordingByTable?.[
                                      String(table.tableNumber)
                                    ]
                                  )}
                                  replayStatus={
                                    pendingByTable?.[
                                      String(table.tableNumber)
                                    ] || null
                                  }
                                  replayNotice={
                                    noticeByTable?.[
                                      String(table.tableNumber)
                                    ] || null
                                  }
                                  onReplayTable={(payload) =>
                                    onReplayTable?.({
                                      tableNumber:
                                        payload?.tableNumber ??
                                        table.tableNumber,
                                    })
                                  }
                                  evaluationVideoCount={
                                    pickingInlineMove
                                      ? 0
                                      : evaluationVideosByTable?.[
                                          String(table.tableNumber)
                                        ]?.length || 0
                                  }
                                  onPlayEvaluationVideo={onPlayEvaluationVideo}
                                  hrTrendMode={
                                    !pickingInlineMove &&
                                    Boolean(hrTrendTables[tableKey])
                                  }
                                  onToggleHrTrend={() =>
                                    toggleHrTrendForTable(tableKey)
                                  }
                                  player1Samples={resolvePlayerSamples(
                                    table.player1
                                  )}
                                  player2Samples={resolvePlayerSamples(
                                    table.player2
                                  )}
                                  player1LiveBpm={resolvePlayerLiveBpm(
                                    table.player1
                                  )}
                                  player2LiveBpm={resolvePlayerLiveBpm(
                                    table.player2
                                  )}
                                  mhr={coachMhr}
                                  onEditPersonalizedPlayer={
                                    pickingInlineMove
                                      ? null
                                      : onEditPersonalizedPlayer
                                  }
                                />
                              );
                            })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {pageCount > 1 ? (
                <button
                  type="button"
                  className="lt-coach-tables-dialog__edge lt-coach-tables-dialog__edge--next"
                  onClick={() => goToPage(safePageIndex + 1)}
                  disabled={safePageIndex >= pageCount - 1}
                  aria-label={t("next", "Next")}
                >
                  <ChevronRightIcon />
                </button>
              ) : null}
            </div>
          )}
          {pageCount > 1 ? (
            <div className="lt-coach-tables-dialog__page-meta" aria-live="polite">
              <span>
                {(() => {
                  const first = pages[safePageIndex]?.[0];
                  const last =
                    pages[safePageIndex]?.[pages[safePageIndex].length - 1];
                  const from =
                    Number(first?.tableNumber ?? first?.table_number) ||
                    safePageIndex * tablesPerPage + 1;
                  const to =
                    Number(last?.tableNumber ?? last?.table_number) || from;
                  return t(
                    "liveTrainingCoachTablesPage",
                    "Tables {{from}}–{{to}} · swipe for more",
                    { from, to, current: safePageIndex + 1, total: pageCount }
                  );
                })()}
              </span>
              <div className="lt-coach-tables-dialog__dots" role="tablist">
                {pages.map((_, i) => (
                  <button
                    key={`dot-${i}`}
                    type="button"
                    className={`lt-coach-tables-dialog__dot${
                      i === safePageIndex
                        ? " lt-coach-tables-dialog__dot--on"
                        : ""
                    }`}
                    style={{
                      background:
                        i === safePageIndex
                          ? tableAccentColor(i * tablesPerPage + 1, null)
                          : undefined,
                    }}
                    aria-label={t(
                      "liveTrainingCoachTablesGoPage",
                      "Go to page {{n}}",
                      { n: i + 1 }
                    )}
                    aria-selected={i === safePageIndex}
                    onClick={() => goToPage(i)}
                  />
                ))}
              </div>
            </div>
          ) : null}
          </div>
        </div>

        <footer className="lt-coach-tables-dialog__footer lt-coach-tables-dialog__caption-dock">
          <div className="lt-coach-tables-dialog__caption-slot">
            {recordingHint ? (
              <p
                className={`lt-coach-tables-dialog__seating-msg lt-coach-tables-dialog__recording-hint${
                  recordingHint.live
                    ? " lt-coach-tables-dialog__recording-hint--live"
                    : ""
                }`}
                role="status"
                aria-live="polite"
                title={recordingHint.text}
              >
                {recordingHint.text}
              </p>
            ) : (
            <p
              className={`lt-coach-tables-dialog__seating-msg${
                seatingMsg || (inlineMoveEnabled && pickingInlineMove)
                  ? ""
                  : " is-empty"
              }`}
              role="status"
              aria-live="polite"
            >
              {seatingMsg ||
                (inlineMoveEnabled && pickingInlineMove
                  ? movingToOtherStation
                    ? t("liveTrainingTableMoveOrSwapDest", {
                        defaultValue:
                          "Tap a table to move. Tap a player on that station to swap.",
                      })
                    : t("liveTrainingTableMoveSourceSelected", {
                        name: moveSelectedPlayers.map((p) => p.name).join(", "),
                        defaultValue: `Selected: ${moveSelectedPlayers
                          .map((p) => p.name)
                          .join(", ")} — tap a table`,
                      })
                  : inlineMoveEnabled
                    ? multiStation
                      ? t("liveTrainingTableMoveStationHint") ||
                        "Tap a player, choose a station, then tap a table to move or a player to swap."
                      : t("liveTrainingTableMoveInlineHint") ||
                        "Tap a player, then tap a table to move them."
                    : "\u00a0")}
            </p>
            )}
          </div>
        </footer>

        <LiveTrainingTableSwapDialog
          open={swapDialogOpen}
          onClose={() => setSwapDialogOpen(false)}
          tables={seatingTables}
          tableSetup={tableSetup}
          exerciseLocked={exerciseLocked || !seatingActionsEnabled}
          roomCapacity={tableCapacity}
          usedTableCount={usedTableCount}
          stations={stations}
          focusedTsgId={focusedTsgId}
          stationLabelsById={stationLabelsById}
          teId={teId}
          elevated={elevated}
        />
        <LiveTrainingTableMoveDialog
          open={moveDialogOpen}
          onClose={() => {
            setMoveDialogOpen(false);
            setMoveSelectedPlayers([]);
          }}
          tables={seatingTables}
          roster={presentPlayers?.length ? presentPlayers : roster}
          otherStationPlayers={otherStationPlayers}
          stationLabelsById={stationLabelsById}
          stations={stations}
          focusedTsgId={focusedTsgId}
          teId={teId}
          tableSetup={tableSetup}
          exerciseLocked={exerciseLocked || !seatingActionsEnabled}
          roomCapacity={tableCapacity}
          usedTableCount={usedTableCount}
          onSelectionChange={setMoveSelectedPlayers}
          initialSelectedPlayers={moveSelectedPlayers}
          elevated={elevated}
        />
      </div>
    </div>,
    portalRoot
  );
}

export default LiveTrainingCoachTablesDialog;
